import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const CHANNEL_NAME = 'devotionfund-tab-sync';

/**
 * Detects when the same URL is opened in multiple browser windows.
 * If the URL is already open in another window, that window and tab
 * are focused and the current tab shows a notification.
 *
 * Uses BroadcastChannel for cross-tab communication and window.focus()
 * to activate the existing window/tab.
 */
export function useTabSync() {
  const location = useLocation();
  const tabIdRef = useRef(crypto.randomUUID());
  const currentPathRef = useRef(location.pathname);

  // Keep current path in sync so the listener always has the latest value
  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;

    const tabId = tabIdRef.current;
    const channel = new BroadcastChannel(CHANNEL_NAME);
    let handled = false;

    channel.onmessage = (event) => {
      const { type, path, senderId } = event.data;
      if (senderId === tabId) return;

      if (type === 'TAB_CHECK' && path === currentPathRef.current) {
        // Another tab opened the same URL — announce ourselves and focus this window/tab
        channel.postMessage({
          type: 'TAB_EXISTS',
          path,
          senderId: tabId,
          targetId: senderId,
        });
        window.focus();
      }

      if (type === 'TAB_EXISTS' && event.data.targetId === tabId && !handled) {
        // Our URL is already open in another window
        handled = true;
        toast.info('This page is already open in another window. Switching to it.');
      }
    };

    // On initial page load, check if this path is already open elsewhere
    channel.postMessage({
      type: 'TAB_CHECK',
      path: location.pathname,
      senderId: tabId,
    });

    return () => {
      channel.close();
    };
    // Only run on mount — full page loads (typing a URL, refresh, bookmark)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
